import {
  AttachInternals,
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Method,
  Prop,
  State,
  Watch,
} from '@stencil/core'
import {
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isValid,
  parse,
  startOfMonth,
  startOfWeek,
} from 'date-fns'

import { CssClassMap } from '../../utils/interfaces'

/**
 * Tipos de entrada de tempo disponíveis no componente datetime-picker.
 */
const INPUT_TIME_TYPE = {
  HOUR: 'hour',
  MINUTE: 'minute',
}

/**
 * Ações de navegação disponíveis no componente datetime-picker.
 */
const NAVIGATION_ACTION = {
  PREVIOUS_HOUR: 'previous-hour',
  PREVIOUS_MINUTE: 'previous-minute',
  PREVIOUS_MONTH: 'previous-month',
  NEXT_HOUR: 'next-hour',
  NEXT_MINUTE: 'next-minute',
  NEXT_MONTH: 'next-month',
} as const

/**
 * Função que define o tipo de um getter para mapeamento de classes CSS.
 */
type CssClassMapGetter = (...args: unknown[]) => CssClassMap

/**
 * Tipo que representa os modos disponíveis no componente datetime-picker.
 */
type DatetimePickerMode = 'date' | 'time' | 'datetime'

/**
 * Tipo que representa os tipos de entrada de tempo disponíveis no componente datetime-picker.
 */
type InputTimeType = (typeof INPUT_TIME_TYPE)[keyof typeof INPUT_TIME_TYPE]

/**
 * Tipo que representa as ações de navegação disponíveis no componente datetime-picker.
 */
type NavigationAction = (typeof NAVIGATION_ACTION)[keyof typeof NAVIGATION_ACTION]

/**
 * Índice do dia da semana.
 * 0 representa domingo, 1 representa segunda-feira, e assim por diante até 6 que representa sábado.
 */
type WeekDayIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6

/**
 * Para uma descrição detalhada, consulte a [documentação do GovBR-DS](https://www.gov.br/ds/components/datetime-picker?tab=designer).
 */
@Component({
  tag: 'br-datetime-picker',
  styleUrl: 'datetime-picker.scss',
  shadow: true,
  formAssociated: true,
})
export class DatetimePicker {
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @Element() el: HTMLBrDatetimePickerElement

  /**
   * Internals do elemento para manipulação de formulários.
   */
  @AttachInternals() elementInternals: ElementInternals

  /**
   * Define se a data atual deve ser selecionada automaticamente ao abrir o datetime-picker.
   * O padrão é false
   */
  @Prop({ reflect: true }) autoSelectToday = false

  /**
   * Define se o datetime-picker está desabilitado.
   */
  @Prop({ reflect: true }) disabled: boolean = false

  /**
   * Define o modo do datetime-picker.
   * Os valores possíveis são:
   * - "date": Permite selecionar apenas a data.
   * - "time": Permite selecionar apenas o horário.
   * - "datetime": Permite selecionar tanto a data quanto o horário.
   */
  @Prop({ reflect: true }) mode: DatetimePickerMode = 'datetime'

  /**
   * Define o nome do campo do datetime-picker.
   * Este nome será utilizado ao submeter o formulário que contém o datetime-picker.
   */
  @Prop({ reflect: true }) name: string

  /**
   * Define o placeholder do campo de entrada do datetime-picker.
   */
  @Prop() placeholder: string

  /**
   * Define o valor inicial do datetime-picker.
   * Pode ser uma instância de Date ou null.
   * Se não for fornecido, o valor padrão será null.
   */
  @Prop() value?: Date | null

  /**
   * Define o dia da semana em que a semana começa.
   * Os valores possíveis são:
   * 0 - Domingo
   * 1 - Segunda-feira
   * 2 - Terça-feira
   * 3 - Quarta-feira
   * 4 - Quinta-feira
   * 5 - Sexta-feira
   * 6 - Sábado
   */
  @Prop({ reflect: true }) weekStartsOn: WeekDayIndex = 0

  /**
   * Data base atual para o calendário.
   */
  @State() currentBaseDate: Date = new Date()

  /**
   * Data selecionada no componente datetime-picker.
   */
  @State() currentSelectedDate: Date | null = null

  /**
   * Valor do campo de entrada.
   */
  @State() private inputValue = ''

  /**
   * Indica se o calendário está aberto ou fechado.
   */
  @State() private isOpen = false

  /**
   * Rascunho dos valores de tempo (hora e minuto) durante a edição.
   */
  @State() private timeDraft: Partial<Record<InputTimeType, string>> = {}

  /**
   * Evento disparado quando a data selecionada é alterada, tanto por interação do usuário quanto por métodos programáticos.
   * @returns Data selecionada ou null se nenhuma data estiver selecionada
   */
  @Event({
    eventName: 'brDateTimeChange',
    bubbles: true,
    composed: true,
  })
  brDateTimeChange: EventEmitter<Date | null>

  /**
   * Manipulador de mudanças na data selecionada.
   * @param newValue Nova data selecionada
   * @internal Used by Stencil's @Watch decorator
   */
  @Watch('currentSelectedDate')
  private handleSelectedDateChange(newValue: Date | null) {
    const formattedValue = this.formatDateForInput(newValue)

    this.inputValue = formattedValue

    this.elementInternals.setFormValue(newValue !== null ? formattedValue : null)

    this.brDateTimeChange.emit(newValue)
  }

  /**
   * Manipulador de mudanças na propriedade `disabled`.
   * @param disabled Indica se o componente está desabilitado
   * @internal Used by Stencil's @Watch decorator
   */
  @Watch('disabled')
  handleDisabledChange(disabled: boolean) {
    if (disabled) {
      this.elementInternals.setFormValue(null)
    } else {
      this.elementInternals.setFormValue(this.inputValue || null)
    }
  }

  /**
   * Manipulador de mudanças no estado de abertura do calendário.
   * @param open Indica se o calendário está aberto
   * @internal Used by Stencil's @Watch decorator
   */
  @Watch('isOpen')
  private handleOpenChange(open: boolean) {
    if (open) {
      document.addEventListener('click', this.handleOutsideClick)
      document.addEventListener('keydown', this.handkeKeydown)
    } else {
      document.removeEventListener('click', this.handleOutsideClick)
      document.removeEventListener('keydown', this.handkeKeydown)
    }
  }

  /**
   * Manipulador de mudanças na propriedade `mode`.
   * @internal Used by Stencil's @Watch decorator
   */
  @Watch('mode')
  handleModeChange() {
    this.resolvePlaceholderFromMode()
  }

  /**
   * Manipulador de mudanças no valor da propriedade `value`.
   */
  @Watch('value')
  protected handleValueChange(newValue: Date | null) {
    if (newValue === null) {
      if (!this.autoSelectToday) {
        this.currentSelectedDate = null
      }
      return
    }

    const date = new Date(newValue)
    this.currentBaseDate = date
    this.currentSelectedDate = new Date(date)
  }

  /**
   * Registro de mapeamentos de classes CSS para diferentes partes do componente.
   * Cada entrada no registro é uma função que retorna um objeto de mapeamento de classes CSS.
   */
  private cssMapRegistry: Record<string, CssClassMapGetter> = {
    calendar: () => {
      return {
        calendar: true,
      }
    },
    day: (date: Date) => {
      return {
        day: true,
        'outside-month': !isSameMonth(date, this.baseDate),
        today: isSameDay(date, new Date()),
        selected: isSameDay(date, this.currentSelectedDate),
      }
    },
    header: () => {
      return {
        header: true,
      }
    },
    month: () => {
      return {
        monthdays: true,
      }
    },
    root: () => {
      return {
        'br-datetime-picker': true,
        datetime: this.mode === 'datetime',
        date: this.mode === 'date',
        time: this.mode === 'time',
      }
    },
    timer: () => {
      return {
        'time-section': true,
      }
    },
    'time-field': () => {
      return {
        'time-field': true,
      }
    },
  }

  /**
   * Array com os nomes dos meses do ano em português.
   */
  private readonly MONTHS = [
    'Janeiro',
    'Fevereiro',
    'Março',
    'Abril',
    'Maio',
    'Junho',
    'Julho',
    'Agosto',
    'Setembro',
    'Outubro',
    'Novembro',
    'Dezembro',
  ]

  /**
   * Array com os nomes dos dias da semana abreviados em português.
   */
  private readonly WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']

  /**
   * Getter: baseDate
   * Retorna a data base para o calendário, que é a data atual.
   * @returns Data atual
   */
  private get baseDate(): Date {
    return this.currentBaseDate
  }

  /**
   * Mapeamento de manipuladores de clique para cada dia.
   * Utiliza WeakMap para associar cada data a sua respectiva função de clique.
   */
  private dayClickHandlers = new WeakMap<Date, (event: MouseEvent) => void>()

  /**
   * Mapeamento de manipuladores de mudança para os campos de tempo (hora e minuto).
   * Utiliza Map para associar cada campo de tempo ao seu respectivo manipulador de mudança.
   */
  private timeInputChangeHandlers = new Map<InputTimeType, (event: Event) => void>()

  /**
   * Mapeamento de manipuladores de commit para os campos de tempo (hora e minuto).
   * Utiliza Map para associar cada campo de tempo ao seu respectivo manipulador de commit.
   */
  private timeInputCommitHandlers = new Map<InputTimeType, (event: Event) => void>()

  /**
   * Mapeamento de manipuladores de keydown para os campos de tempo (hora e minuto).
   * Utiliza Map para associar cada campo de tempo ao seu respectivo manipulador de keydown.
   */
  private timeInputKeydownHandlers = new Map<InputTimeType, (event: KeyboardEvent) => void>()

  /**
   * Ciclo de vida do componente: Executado antes do componente ser carregado.
   */
  componentWillLoad() {
    this.resolvePlaceholderFromMode()
    this.initializeFromValue()

    // referência proposital para o TS/ESLint
    void this.handleSelectedDateChange

    // referência proposital para o TS/ESLint
    void this.handleOpenChange

    // referência proposital para o TS/ESLint
    void this.handleValueChange
  }

  /**
   * Ciclo de vida do componente: Executado quando o componente é desconectado do DOM.
   */
  disconnectedCallback() {
    document.removeEventListener('click', this.handleOutsideClick)
  }

  /**
   * Fecha o dropdown do calendário.
   */
  @Method()
  async close() {
    this.isOpen = false
  }

  /**
   * Retorna a data atualmente selecionada no datetime-picker.
   */
  @Method()
  async getValue(): Promise<Date | null> {
    return this.currentSelectedDate
  }

  /**
   * Abre o dropdown do calendário.
   */
  @Method()
  async open() {
    this.isOpen = true
  }

  /**
   * Reseta o estado interno do componente para os valores iniciais.
   */
  @Method()
  async reset() {
    this.resetInternal()
  }

  /**
   * Define a data selecionada no datetime-picker.
   * @param value Data a ser definida ou null para resetar o valor
   */
  @Method()
  async setValue(value: Date | null) {
    if (value === null) {
      this.resetInternal()
      return
    }

    const date = new Date(value)
    this.currentBaseDate = date
    this.currentSelectedDate = date
  }

  /**
   * Alterna o estado do dropdown do calendário entre aberto e fechado.
   */
  @Method()
  async toggle() {
    this.isOpen = !this.isOpen
  }

  /**
   * Manipulador de reset do formulário.
   */
  formResetCallback() {
    this.resetInternal()
  }

  /**
   * Manipulador de clique para um dia específico.
   * @param event Evento de clique
   * @param date Data do dia clicado
   */
  private handleDayClick(date: Date) {
    if (this.disabled) return
    if (this.mode === 'time') return

    const selected = new Date(date)
    selected.setHours(this.currentBaseDate.getHours(), this.currentBaseDate.getMinutes(), 0, 0)

    this.currentBaseDate = selected
    this.currentSelectedDate = selected
  }

  /**
   * Manipulador de evento blur do campo de entrada.
   */
  private handleInputBlur = () => {
    this.commitInputValue()
  }

  /**
   * Manipulador de mudança do campo de entrada.
   * @param event Evento de mudança
   */
  private handleInputChange = (event: Event) => {
    const input = event.target as HTMLInputElement
    this.inputValue = input.value
  }

  /**
   * Manipulador de evento keydown do campo de entrada.
   * @param event Evento de keydown
   */
  private handleInputKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      this.commitInputValue()
    }
  }

  /**
   * Manipulador de evento keydown global para fechar o calendário com a tecla Escape.
   */
  private handkeKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      this.isOpen = false
    }
  }

  /**
   * Manipulador de mudança de mês.
   * @param event Evento de mudança
   */
  private handleMonthChange = (event: Event) => {
    const select = event.target as HTMLSelectElement
    const monthIndex = Number(select.value)

    if (Number.isNaN(monthIndex)) return

    const date = new Date(this.currentBaseDate)
    date.setMonth(monthIndex, 1)
    this.currentBaseDate = date
  }

  /**
   * Manipulador de clique para os botões de navegação do calendário.
   * @param event Evento de clique
   */
  private handleNavigationClick = (event: MouseEvent) => {
    if (this.disabled) return

    const action = (event.currentTarget as HTMLElement).dataset.action as NavigationAction | undefined

    if (action === undefined) return

    switch (action) {
      case NAVIGATION_ACTION.PREVIOUS_HOUR:
        this.decreaseHour()
        break
      case NAVIGATION_ACTION.PREVIOUS_MINUTE:
        this.decreaseMinute()
        break
      case NAVIGATION_ACTION.PREVIOUS_MONTH:
        this.decreaseMonth()
        break
      case NAVIGATION_ACTION.NEXT_HOUR:
        this.increaseHour()
        break
      case NAVIGATION_ACTION.NEXT_MINUTE:
        this.increaseMinute()
        break
      case NAVIGATION_ACTION.NEXT_MONTH:
        this.increaseMonth()
        break
      default:
        break
    }
  }

  /**
   * Manipulador de clique fora do componente para fechar o dropdown.
   * @param event Evento de clique
   */
  private handleOutsideClick = (event: MouseEvent) => {
    if (!this.el.contains(event.target as Node)) {
      this.isOpen = false
    }
  }

  /**
   * Manipulador de mudança do campo de tempo (hora ou minuto).
   * @param event Evento de mudança
   * @param type Tipo de campo de tempo (hora ou minuto)
   */
  private handleTimeInputChange = (event: Event, type: InputTimeType) => {
    if (this.disabled) return
    const input = event.target as HTMLInputElement
    const value = input.value.replace(/\D/g, '')
    this.timeDraft[type] = value
  }

  /**
   * Manipulador de commit do campo de tempo (hora ou minuto).
   * @param event Evento de commit
   * @param type Tipo de campo de tempo (hora ou minuto)
   */
  private handleTimeInputCommit(type: InputTimeType) {
    if (this.disabled) return
    const draft = this.timeDraft[type]
    if (draft == null || draft === '') return

    const numeric = Number(draft)
    if (Number.isNaN(numeric)) return

    const date = new Date(this.currentBaseDate)

    if (type === INPUT_TIME_TYPE.HOUR) {
      if (numeric < 0 || numeric > 23) return
      date.setHours(numeric)
    }

    if (type === INPUT_TIME_TYPE.MINUTE) {
      if (numeric < 0 || numeric > 59) return
      date.setMinutes(numeric)
    }

    this.currentBaseDate = date
    this.currentSelectedDate = new Date(date)

    this.timeDraft[type] = undefined
  }

  /**
   * Manipulador de alternância do dropdown do calendário.
   * @param event Evento de clique
   */
  private handleToggleDropdown = (event: MouseEvent) => {
    if (this.disabled) return
    event.stopPropagation()
    this.isOpen = !this.isOpen
  }

  /**
   * Manipulador de mudança de ano.
   * @param event Evento de mudança
   */
  private handleYearChange = (event: Event) => {
    if (this.mode === 'time') return

    const input = event.target as HTMLInputElement
    const year = Number(input.value)

    if (Number.isNaN(year)) return

    const date = new Date(this.currentBaseDate)
    date.setFullYear(year, date.getMonth(), 1)

    this.currentBaseDate = date
  }

  /**
   * Atualiza o valor do input com base no valor atual selecionado.
   */
  private commitInputValue() {
    const value = this.inputValue.trim()
    if (value === '') return

    const parsed = this.parseInputValue(value)
    if (parsed === null) return

    this.currentSelectedDate = new Date(parsed)
    this.currentBaseDate = new Date(parsed)
    // this.inputValue = this.formatDateForInput(parsed)
  }

  /**
   * Navega para a hora anterior no timer.
   */
  private decreaseHour() {
    if (this.mode === 'date') return

    const date = new Date(this.currentBaseDate)
    date.setHours(date.getHours() - 1)

    this.currentBaseDate = date
    this.currentSelectedDate = new Date(date)
  }

  /**
   * Navega para o minuto anterior no timer.
   */
  private decreaseMinute() {
    if (this.mode === 'date') return

    const date = new Date(this.currentBaseDate)
    date.setMinutes(date.getMinutes() - 1)

    this.currentBaseDate = date
    this.currentSelectedDate = new Date(date)
  }

  /**
   * Navega para o mês anterior no calendário.
   */
  private decreaseMonth() {
    if (this.mode === 'time') return

    const date = new Date(this.currentBaseDate)
    date.setFullYear(date.getFullYear(), date.getMonth() - 1, 1)

    this.currentBaseDate = date
  }

  /**
   * Retorna o valor do campo de entrada formatado.
   * @returns Valor formatado do campo de entrada
   */
  private formatDateForInput(date?: Date | null): string {
    if (date == null || !isValid(date)) return ''

    switch (this.mode) {
      case 'time':
        return format(date, 'HH:mm')

      case 'date':
        return format(date, 'dd/MM/yyyy')

      case 'datetime':
        return format(date, 'dd/MM/yyyy HH:mm')

      default:
        return ''
    }
  }

  /**
   * Retorna o mapeamento de classes CSS para um determinado nome.
   * @param name Nome da parte do componente para a qual o mapeamento de classes será obtido
   * @param args Argumentos adicionais que podem ser necessários para a função de mapeamento de classes
   * @returns Objeto contendo o mapeamento de classes CSS
   */
  private getCss(name: keyof typeof this.cssMapRegistry, ...args: unknown[]): CssClassMap {
    const mapGetter = this.cssMapRegistry[name]
    return mapGetter !== undefined ? mapGetter(...args) : {}
  }

  /**
   * Retorna o manipulador de clique para um dia específico.
   * Se o manipulador ainda não existir, ele é criado e armazenado no WeakMap.
   * @param date Data do dia para o qual o manipulador será obtido
   * @returns Função manipuladora de clique para o dia especificado
   */
  private getDayClickHandler(date: Date) {
    let handler = this.dayClickHandlers.get(date)
    if (handler === undefined) {
      handler = () => this.handleDayClick(date)
      this.dayClickHandlers.set(date, handler)
    }
    return handler
  }

  /**
   * Retorna o dia do mês com zero à esquerda, se necessário.
   * @param date Data para a qual o dia do mês será obtido
   * @returns String representando o dia do mês com dois dígitos
   */
  private getDayWithLeadingZero(date: Date): string {
    return format(date, 'dd')
  }

  /**
   * Retorna o ícone correspondente a uma ação de navegação.
   * @param action Ação de navegação para a qual o ícone será obtido
   * @returns Nome do ícone correspondente à ação de navegação
   */
  private getIconForAction(action: NavigationAction): string {
    switch (action) {
      case NAVIGATION_ACTION.PREVIOUS_HOUR:
      case NAVIGATION_ACTION.PREVIOUS_MINUTE:
        return 'fa6-solid:chevron-down'

      case NAVIGATION_ACTION.PREVIOUS_MONTH:
        return 'fa6-solid:chevron-left'

      case NAVIGATION_ACTION.NEXT_HOUR:
      case NAVIGATION_ACTION.NEXT_MINUTE:
        return 'fa6-solid:chevron-up'

      case NAVIGATION_ACTION.NEXT_MONTH:
        return 'fa6-solid:chevron-right'
      default:
        return ''
    }
  }

  /**
   * Resolve o placeholder do campo de entrada com base no modo atual.
   */
  private getInputIcon() {
    switch (this.mode) {
      case 'time':
        return 'fa7-solid:clock'
      case 'date':
      case 'datetime':
      default:
        return 'fa6-solid:calendar-days'
    }
  }

  /**
   * Retorna as datas que serão exibidas na visualização do mês.
   * @returns
   */
  private getMonthViewDates(): Date[] {
    const start = startOfWeek(startOfMonth(this.baseDate), { weekStartsOn: this.weekStartsOn })
    const end = endOfWeek(endOfMonth(this.baseDate), { weekStartsOn: this.weekStartsOn })

    return eachDayOfInterval({ start, end })
  }

  /**
   * Retorna o rótulo do mês e ano para uma determinada data.
   * @param date Data para a qual o rótulo será obtido
   * @returns Rótulo do mês e ano no formato "MMMM yyyy"
   */
  private getMonthYearLabel(date: Date) {
    return format(date, 'MMMM yyyy')
  }

  /**
   * Retorna o rótulo aria para um botão de navegação específico.
   * @param action Ação de navegação para a qual o rótulo será obtido
   * @returns Rótulo aria correspondente à ação de navegação
   */
  private getNavigationAriaLabel(action: NavigationAction) {
    const base = this.currentBaseDate

    switch (action) {
      case NAVIGATION_ACTION.PREVIOUS_HOUR:
        return `Ir para a hora anterior, ${this.currentBaseDate.getHours() - 1}`

      case NAVIGATION_ACTION.PREVIOUS_MINUTE:
        return `Ir para o minuto anterior, ${this.currentBaseDate.getMinutes() - 1}`

      case NAVIGATION_ACTION.PREVIOUS_MONTH:
        return `Ir para o mês anterior, ${this.getMonthYearLabel(new Date(base.getFullYear(), base.getMonth() - 1, 1))}`

      case NAVIGATION_ACTION.NEXT_HOUR:
        return `Ir para a próxima hora, ${this.currentBaseDate.getHours() + 1}`

      case NAVIGATION_ACTION.NEXT_MINUTE:
        return `Ir para o próximo minuto, ${this.currentBaseDate.getMinutes() + 1}`

      case NAVIGATION_ACTION.NEXT_MONTH:
        return `Ir para o próximo mês, ${this.getMonthYearLabel(new Date(base.getFullYear(), base.getMonth() + 1, 1))}`
      default:
        return ''
    }
  }

  /**
   * Retorna o rótulo aria para um campo de tempo específico.
   * @param type Tipo de campo de tempo (hora ou minuto)
   * @returns Rótulo aria correspondente ao campo de tempo
   */
  private getTimeFieldAriaLabel(type: InputTimeType): string {
    switch (type) {
      case INPUT_TIME_TYPE.HOUR:
        return 'Hora'
      case INPUT_TIME_TYPE.MINUTE:
        return 'Minuto'
      default:
        return ''
    }
  }

  /**
   * Retorna a configuração do campo de tempo com base no tipo especificado.
   * @param type Tipo de campo de tempo (hora ou minuto)
   * @returns Objeto contendo o valor atual e as ações de navegação para o campo de tempo
   */
  private getTimeFieldConfig(type: InputTimeType) {
    switch (type) {
      case INPUT_TIME_TYPE.HOUR:
        return {
          value: this.currentBaseDate.getHours(),
          nextAction: NAVIGATION_ACTION.NEXT_HOUR,
          prevAction: NAVIGATION_ACTION.PREVIOUS_HOUR,
        }
      case INPUT_TIME_TYPE.MINUTE:
        return {
          value: this.currentBaseDate.getMinutes(),
          nextAction: NAVIGATION_ACTION.NEXT_MINUTE,
          prevAction: NAVIGATION_ACTION.PREVIOUS_MINUTE,
        }
      default:
        return {
          value: 0,
          nextAction: NAVIGATION_ACTION.NEXT_HOUR,
          prevAction: NAVIGATION_ACTION.PREVIOUS_HOUR,
        }
    }
  }

  /**
   * Retorna o valor do campo de tempo formatado com dois dígitos.
   * @param type Tipo de campo de tempo (hora ou minuto)
   * @returns Valor formatado do campo de tempo
   */
  private getTimeFieldValue(type: InputTimeType): string {
    switch (type) {
      case INPUT_TIME_TYPE.HOUR:
        return this.currentBaseDate.getHours().toString().padStart(2, '0')
      case INPUT_TIME_TYPE.MINUTE:
        return this.currentBaseDate.getMinutes().toString().padStart(2, '0')
      default:
        return '00'
    }
  }

  /**
   * Retorna o manipulador de commit para um campo de tempo específico.
   */
  private getTimeInputChangeHandler(type: InputTimeType) {
    let handler = this.timeInputChangeHandlers.get(type)

    if (handler === undefined) {
      handler = (event: Event) => this.handleTimeInputChange(event, type)
      this.timeInputChangeHandlers.set(type, handler)
    }

    return handler
  }

  /**
   * Retorna o manipulador de commit para um campo de tempo específico.
   */
  private getTimeInputCommitHandler(type: InputTimeType) {
    let handler = this.timeInputCommitHandlers.get(type)

    if (handler === undefined) {
      handler = () => this.handleTimeInputCommit(type)
      this.timeInputCommitHandlers.set(type, handler)
    }

    return handler
  }

  /**
   * Retorna o manipulador de keydown para um campo de tempo específico.
   */
  private getTimeInputKeydownHandler(type: InputTimeType) {
    let handler = this.timeInputKeydownHandlers.get(type)
    if (handler === undefined) {
      handler = (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
          this.handleTimeInputCommit(type)
        }
      }
      this.timeInputKeydownHandlers.set(type, handler)
    }
    return handler
  }

  /**
   * Retorna os rótulos dos dias da semana, ajustados conforme o dia de início da semana.
   * @returns
   */
  private getWeekdayLabels(): string[] {
    const start = this.weekStartsOn
    return [...this.WEEKDAYS.slice(start), ...this.WEEKDAYS.slice(0, start)]
  }

  /**
   * Navega para a próxima hora no timer.
   */
  private increaseHour() {
    if (this.mode === 'date') return

    const date = new Date(this.currentBaseDate)
    date.setHours(date.getHours() + 1)

    this.currentBaseDate = date
    this.currentSelectedDate = new Date(date)
  }

  /**
   * Navega para o próximo minuto no timer.
   */
  private increaseMinute() {
    if (this.mode === 'date') return

    const date = new Date(this.currentBaseDate)
    date.setMinutes(date.getMinutes() + 1)

    this.currentBaseDate = date
    this.currentSelectedDate = new Date(date)
  }

  /**
   * Navega para o próximo mês no calendário.
   */
  private increaseMonth() {
    if (this.mode === 'time') return

    const date = new Date(this.currentBaseDate)
    date.setFullYear(date.getFullYear(), date.getMonth() + 1, 1)

    this.currentBaseDate = date
  }

  /**
   * Inicializa o estado interno do componente com base no valor da propriedade `value`.
   * Se `value` estiver definido, usa esse valor.
   * Caso contrário, se `autoSelectToday` for verdadeiro, seleciona a data/hora atual.
   * Caso contrário, inicializa com estado vazio.
   */
  private initializeFromValue() {
    if (this.value != null) {
      const date = new Date(this.value)
      this.currentBaseDate = date
      this.currentSelectedDate = new Date(date)
      return
    }

    if (this.autoSelectToday) {
      const now = new Date()
      this.currentBaseDate = now
      this.currentSelectedDate = new Date(now)
      return
    }

    this.currentBaseDate = new Date()
    this.currentSelectedDate = null
  }

  /**
   * Analisa o valor de entrada de data e retorna um objeto Date ou null se inválido.
   * @param value Valor de entrada a ser analisado
   * @param base Data base para análise
   * @returns Objeto Date válido ou null se inválido
   */
  private parseDateInput(value: string, base: Date): Date | null {
    const parsedDate = parse(value, 'dd/MM/yyyy', base)
    if (!isValid(parsedDate)) return null

    parsedDate.setHours(base.getHours(), base.getMinutes(), 0, 0)

    return parsedDate
  }

  /**
   * Analisa o valor de entrada de data e hora e retorna um objeto Date ou null se inválido.
   * @param value Valor de entrada a ser analisado
   * @param base Data base para análise
   * @returns Objeto Date válido ou null se inválido
   */
  private parseDateTimeInput(value: string, base: Date): Date | null {
    // data + hora
    const full = parse(value, 'dd/MM/yyyy HH:mm', base)
    if (isValid(full)) return full

    // só data → hora atual
    const date = this.parseDateInput(value, base)
    if (date !== null) {
      date.setHours(base.getHours(), base.getMinutes(), 0, 0)
      return date
    }

    // só hora → data atual
    return this.parseTimeInput(value, base)
  }

  /**
   * Analisa o valor de entrada e retorna um objeto Date ou null se inválido.
   * @param value Valor de entrada a ser analisado
   * @returns Objeto Date válido ou null se inválido
   */
  private parseInputValue(value: string): Date | null {
    const base = this.currentSelectedDate ?? new Date()

    switch (this.mode) {
      case 'date':
        return this.parseDateInput(value, base)

      case 'time':
        return this.parseTimeInput(value, base)

      case 'datetime':
        return this.parseDateTimeInput(value, base)

      default:
        return null
    }
  }

  /**
   * Analisa o valor de entrada de tempo e retorna um objeto Date ou null se inválido.
   * @param value Valor de entrada a ser analisado
   * @param base Data base para análise
   * @returns Objeto Date válido ou null se inválido
   */
  private parseTimeInput(value: string, base: Date): Date | null {
    const parsedTime = parse(value, 'HH:mm', base)
    if (!isValid(parsedTime)) return null

    const date = new Date(base)
    date.setHours(parsedTime.getHours(), parsedTime.getMinutes(), 0, 0)

    return date
  }

  /**
   * Reseta o estado interno do componente para os valores iniciais.
   */
  private resetInternal() {
    if (this.autoSelectToday) {
      const now = new Date()
      this.currentBaseDate = now
      this.currentSelectedDate = new Date(now)
    } else {
      this.currentBaseDate = new Date()
      this.currentSelectedDate = null
    }
  }

  /**
   * Resolve o placeholder padrão com base no modo atual, se nenhum placeholder personalizado for fornecido.
   */
  private resolvePlaceholderFromMode() {
    if (this.placeholder != null) return

    switch (this.mode) {
      case 'date':
        this.placeholder = 'Selecione uma data'
        break
      case 'time':
        this.placeholder = 'Horário'
        break
      case 'datetime':
        this.placeholder = 'Selecione data e hora'
        break
    }
  }

  /**
   * Renderiza um dia específico (uma célula no calendário).
   * @param date Data do dia a ser renderizado
   * @returns Elemento JSX representando o dia
   */
  private renderDayCell(date: Date) {
    return (
      <div class={this.getCss('day', date)} onClick={this.getDayClickHandler(date)}>
        {this.getDayWithLeadingZero(date)}
      </div>
    )
  }

  /**
   * Renderiza o calendário completo (dias da semana e grade de dias do mês).
   * @returns Elemento JSX representando o calendário
   */
  private renderCalendar() {
    return (
      <div class={this.getCss('calendar')}>
        {this.renderWeekdays()}
        {this.renderMonthGrid()}
      </div>
    )
  }

  /**
   * Renderiza o barra de navegação do cabeçalho.
   * @returns Elemento JSX representando a barra de navegação do cabeçalho.
   */
  private renderCalendarHeader() {
    return (
      <div class={this.getCss('header')}>
        {this.renderNavigationButton(NAVIGATION_ACTION.PREVIOUS_MONTH)}
        {this.renderMonthSelection()}
        {this.renderYearSelection()}
        {this.renderNavigationButton(NAVIGATION_ACTION.NEXT_MONTH)}
      </div>
    )
  }

  /**
   * Renderiza um divisor visual entre o calendário e o time picker.
   * @returns Elemento JSX representando o divisor
   */
  private renderDivider() {
    return <br-divider thickness="medium" color="#e6e6e6" margin-top="10px" margin-bottom="10px"></br-divider>
  }

  private renderInputField() {
    return (
      <br-input
        type="text"
        value={this.inputValue}
        disabled={this.disabled}
        placeholder={this.placeholder}
        onInput={this.handleInputChange}
        onBlur={this.handleInputBlur}
        onKeyDown={this.handleInputKeydown}
        aria-label="Campo de data e hora"
      >
        <br-icon
          slot="action"
          icon-name={this.getInputIcon()}
          aria-hidden="true"
          onClick={this.handleToggleDropdown}
        ></br-icon>
      </br-input>
    )
  }

  /**
   * Renderiza a grade de dias do mês.
   * @returns Elemento JSX representando os dias do mês
   */
  private renderMonthGrid() {
    const dates = this.getMonthViewDates()

    return <div class={this.getCss('month')}>{dates.map(this.renderDayCell, this)}</div>
  }

  /**
   * Renderiza uma opção de mês para seleção.
   * @param month Nome do mês
   * @param index Índice do mês (0-11)
   * @returns Elemento JSX representando a opção de mês
   */
  private renderMonthOption(month: string, index: number) {
    return (
      <option value={index} selected={index === this.currentBaseDate.getMonth()}>
        {month}
      </option>
    )
  }

  /**
   * Renderiza a seleção de mês no cabeçalho do calendário.
   * @returns Elemento JSX representando a seleção de mês
   */
  private renderMonthSelection() {
    return (
      <label class="month-select">
        <span class="visually-hidden">Selecionar mês</span>
        <select onChange={this.handleMonthChange} aria-label="Selecionar mês" disabled={this.disabled}>
          {this.MONTHS.map(this.renderMonthOption, this)}
        </select>
      </label>
    )
  }

  /**
   * Renderiza um botão de navegação no cabeçalho.
   * @param action Ação de navegação associada ao botão
   * @returns Elemento JSX representando o botão de navegação
   */
  private renderNavigationButton(action: NavigationAction) {
    return (
      <br-button
        emphasis="tertiary"
        density="small"
        shape="circle"
        disabled={this.disabled}
        data-action={action}
        aria-label={this.getNavigationAriaLabel(action)}
        onClick={this.handleNavigationClick}
      >
        <br-icon icon-name={this.getIconForAction(action)} aria-hidden="true"></br-icon>
      </br-button>
    )
  }

  /**
   * Renderiza a seção de time picker (hora e minuto).
   * @returns Elemento JSX representando a seção de time picker
   */
  private renderTimePicker() {
    if (this.mode === 'date') return null

    return (
      <div class={this.getCss('timer')}>
        {this.renderTimeField(INPUT_TIME_TYPE.HOUR)}
        <span class="time-separator">:</span>
        {this.renderTimeField(INPUT_TIME_TYPE.MINUTE)}
      </div>
    )
  }

  /**
   * Renderiza um campo de tempo (hora ou minuto).
   * @param type Tipo de campo de tempo (hora ou minuto)
   * @returns Elemento JSX representando o campo de tempo
   */
  private renderTimeField(type: InputTimeType) {
    const { nextAction, prevAction } = this.getTimeFieldConfig(type)

    return (
      <div class={this.getCss('time-field')}>
        {this.renderNavigationButton(nextAction)}
        <input
          type="text"
          inputmode="numeric"
          disabled={this.disabled}
          maxLength={2}
          value={this.timeDraft[type] ?? this.getTimeFieldValue(type)}
          aria-label={this.getTimeFieldAriaLabel(type)}
          onInput={this.getTimeInputChangeHandler(type)}
          onBlur={this.getTimeInputCommitHandler(type)}
          onKeyDown={this.getTimeInputKeydownHandler(type)}
        />
        {this.renderNavigationButton(prevAction)}
      </div>
    )
  }

  /**
   * Renderiza os rótulos dos dias da semana.
   * @returns Elemento JSX representando os rótulos dos dias da semana
   */
  private renderWeekdays() {
    const labels = this.getWeekdayLabels()

    return (
      <div class="weekdays">
        {labels.map((label) => (
          <div class="weekday">{label}</div>
        ))}
      </div>
    )
  }

  private renderYearSelection() {
    return (
      <label class="year-select">
        <span class="visually-hidden">Selecionar ano</span>
        <input
          type="number"
          disabled={this.disabled}
          value={this.currentBaseDate.getFullYear()}
          onChange={this.handleYearChange}
          aria-label="Selecionar ano"
        />
      </label>
    )
  }

  /**
   * Renderiza o componente datetime-picker
   * @returns Elemento JSX representando o componente
   */
  render() {
    return (
      <Host>
        <div class={this.getCss('root')}>
          {this.renderInputField()}
          {this.isOpen && (
            <br-card>
              {this.mode !== 'time' && this.renderCalendarHeader()}
              {this.mode !== 'time' && this.renderCalendar()}
              {this.mode === 'datetime' && this.renderDivider()}
              {this.mode !== 'date' && this.renderTimePicker()}
            </br-card>
          )}
        </div>
      </Host>
    )
  }
}
